import { useAssets } from 'expo-asset';
import type { FC, SyntheticEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EmbeddedWebpageContext } from '@/shared/components/embedded/EmbeddedWebpageContext';
import type { EmbeddedWebpageProps } from '@/shared/components/embedded/EmbeddedWebpageProps';
import { StateView } from '@/shared/components/state-view/StateView';
import { useFetchHtml } from '@/shared/hooks/useFetchHtml';

export const EmbeddedWebpage: FC<EmbeddedWebpageProps> = ({
  srcId,
  src,
  onError,
  onMessageReceived,
  children,
  preventScroll = false
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [assets] = useAssets(srcId);
  const [loaded, setLoaded] = useState(false);

  const logMessage = useCallback((message: string) => {
    if (__DEV__) {
      console.info(message);
    }
  }, []);

  const handleError = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      console.error('Embedded webpage error', event);
      onError?.(event);
    },
    [onError]
  );

  const sendMessage = useCallback(
    (payload: unknown) => {
      logMessage(`sending: ${JSON.stringify(payload)}`);

      iframeRef.current?.contentWindow?.postMessage(payload, '*');
    },
    [logMessage]
  );

  const handleMessage = useCallback(
    (event: MessageEvent<unknown>) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      logMessage(`received message: ${JSON.stringify(event)}`);

      const message = event.data;
      if (!message) {
        console.warn('Received message is empty');
        return;
      }

      logMessage(`received: ${JSON.stringify(message)}`);

      onMessageReceived?.(message);
    },
    [logMessage, onMessageReceived]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  // Not possible due to security restrictions:
  /* useEffect(() => {
    let userAgent = iframeRef.current?.contentWindow?.navigator?.userAgent;
    if (
      userAgent &&
      userAgentPostfix &&
      !userAgent.includes(userAgentPostfix)
    ) {
      userAgent = `${userAgent} ${userAgentPostfix}`;
    }
  }, [userAgentPostfix]); */

  const localContentUri = assets?.[0]?.localUri;

  const isLocal = !src;

  const contentUri = src ?? localContentUri;

  const { isPending, isError } = useFetchHtml(localContentUri);

  if (!contentUri) {
    return;
  }

  return (
    <EmbeddedWebpageContext.Provider value={{ sendMessage }}>
      <StateView
        asView
        isLoading={isLocal && isPending}
        isError={isLocal && isError}
      >
        <iframe
          title="Embedded"
          ref={iframeRef}
          src={contentUri}
          onError={handleError}
          style={{
            height: '100%',
            borderStyle: 'none',
            borderWidth: 0,
            overflow: preventScroll ? 'hidden' : 'auto'
          }}
          onLoad={() => setLoaded(true)}
          scrolling={preventScroll ? 'no' : 'yes'}
        />
        {loaded && children}
      </StateView>
    </EmbeddedWebpageContext.Provider>
  );
};
